import { ReactNode, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  IconArrowsMoveVertical,
  IconChevronDown,
  IconChevronUp,
  IconFold,
  IconMinus,
  IconPlus,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import {
  computeDiffRows,
  computeFolds,
  getChangeBlockStarts,
} from './computeDiffRows';
import { LineCell } from './LineCell';
import { ViewerFrame } from './ViewerFrame';

const CONTEXT_LINES = 8;
const MIN_FOLD_SIZE = 20;
const EXPAND_STEP = 20;

// Number of rows revealed from the top of each fold, keyed by fold start.
type FoldState = Record<number, number>;

type DisplayItem =
  | { kind: 'row'; rowIndex: number }
  | {
      kind: 'fold';
      foldStart: number;
      hiddenCount: number;
      revealedCount: number;
    }
  | { kind: 'collapse'; foldStart: number };

type SideBySideDiffProps = {
  oldText: string;
  newText: string;
  beforeLabel: ReactNode;
  afterLabel: ReactNode;
};

export const SideBySideDiff = ({
  oldText,
  newText,
  beforeLabel,
  afterLabel,
}: SideBySideDiffProps) => {
  const intl = useIntl();

  const rows = useMemo(
    () => computeDiffRows(oldText, newText),
    [oldText, newText]
  );
  const folds = useMemo(
    () => computeFolds(rows, CONTEXT_LINES, MIN_FOLD_SIZE),
    [rows]
  );
  const changeBlockStarts = useMemo(() => getChangeBlockStarts(rows), [rows]);

  const [foldState, setFoldState] = useState<FoldState>({});
  const [currentChange, setCurrentChange] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const displayItems = useMemo<DisplayItem[]>(() => {
    const items: DisplayItem[] = [];
    let rowIndex = 0;

    folds.forEach(fold => {
      while (rowIndex < fold.start) {
        items.push({ kind: 'row', rowIndex });
        rowIndex += 1;
      }

      const revealed = foldState[fold.start] ?? 0;
      const visibleEnd = Math.min(fold.start + revealed, fold.end);

      while (rowIndex < visibleEnd) {
        items.push({ kind: 'row', rowIndex });
        rowIndex += 1;
      }

      if (visibleEnd < fold.end) {
        items.push({
          kind: 'fold',
          foldStart: fold.start,
          hiddenCount: fold.end - visibleEnd,
          revealedCount: revealed,
        });
        rowIndex = fold.end;
      } else {
        items.push({ kind: 'collapse', foldStart: fold.start });
      }
    });

    while (rowIndex < rows.length) {
      items.push({ kind: 'row', rowIndex });
      rowIndex += 1;
    }

    return items;
  }, [rows, folds, foldState]);

  const revealMore = (foldStart: number, count: number) =>
    setFoldState(previous => ({
      ...previous,
      [foldStart]: (previous[foldStart] ?? 0) + count,
    }));

  const revealLess = (foldStart: number, count: number) =>
    setFoldState(previous => ({
      ...previous,
      [foldStart]: Math.max(0, (previous[foldStart] ?? 0) - count),
    }));

  const collapseFold = (foldStart: number) =>
    setFoldState(previous => ({ ...previous, [foldStart]: 0 }));

  const scrollToChange = (changeIndex: number) => {
    setCurrentChange(changeIndex);
    const rowIndex = changeBlockStarts[changeIndex];
    containerRef.current
      ?.querySelector(`[data-row-index="${rowIndex}"]`)
      ?.scrollIntoView({ block: 'center' });
  };

  const goToPreviousChange = () => {
    if (changeBlockStarts.length === 0) return;
    const next =
      currentChange === null
        ? changeBlockStarts.length - 1
        : (currentChange - 1 + changeBlockStarts.length) %
          changeBlockStarts.length;
    scrollToChange(next);
  };

  const goToNextChange = () => {
    if (changeBlockStarts.length === 0) return;
    const next =
      currentChange === null
        ? 0
        : (currentChange + 1) % changeBlockStarts.length;
    scrollToChange(next);
  };

  const foldAction = ({
    title,
    onClick,
    icon,
  }: {
    title: string;
    onClick: () => void;
    icon: ReactNode;
  }) => (
    <Tooltip title={title}>
      <IconButton size="small" onClick={onClick} aria-label={title}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  const header = (
    <>
      <Typography variant="caption" sx={{ flex: 1, fontWeight: 'bold' }}>
        {beforeLabel}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ flex: 1 }}
      >
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          {afterLabel}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            {changeBlockStarts.length === 0
              ? intl.formatMessage({
                  id: 'features.configurationChangelog.detail.diff.noChanges',
                })
              : intl.formatMessage(
                  {
                    id: 'features.configurationChangelog.detail.diff.changeCounter',
                  },
                  {
                    current: currentChange === null ? '–' : currentChange + 1,
                    total: changeBlockStarts.length,
                  }
                )}
          </Typography>
          <IconButton
            size="small"
            onClick={goToPreviousChange}
            disabled={changeBlockStarts.length === 0}
            aria-label={intl.formatMessage({
              id: 'features.configurationChangelog.detail.diff.previousChange',
            })}
          >
            <SvgIcon fontSize="small">
              <IconChevronUp />
            </SvgIcon>
          </IconButton>
          <IconButton
            size="small"
            onClick={goToNextChange}
            disabled={changeBlockStarts.length === 0}
            aria-label={intl.formatMessage({
              id: 'features.configurationChangelog.detail.diff.nextChange',
            })}
          >
            <SvgIcon fontSize="small">
              <IconChevronDown />
            </SvgIcon>
          </IconButton>
        </Stack>
      </Stack>
    </>
  );

  return (
    <ViewerFrame
      header={header}
      gridTemplateColumns="auto minmax(0, 1fr) auto minmax(0, 1fr)"
      containerRef={containerRef}
    >
      {displayItems.map(item =>
        item.kind === 'row' ? (
          <Box
            key={`row-${item.rowIndex}`}
            data-row-index={item.rowIndex}
            sx={{
              display: 'grid',
              gridColumn: '1 / -1',
              gridTemplateColumns: 'subgrid',
            }}
          >
            <LineCell
              cell={rows[item.rowIndex]?.left ?? null}
              emphasis={
                rows[item.rowIndex]?.type === 'change' ? 'removed' : null
              }
            />
            <LineCell
              cell={rows[item.rowIndex]?.right ?? null}
              emphasis={rows[item.rowIndex]?.type === 'change' ? 'added' : null}
              divider
            />
          </Box>
        ) : (
          <Stack
            key={`fold-${item.foldStart}`}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              gridColumn: '1 / -1',
              px: 1,
              py: 0.5,
              bgcolor: 'action.hover',
              borderTop: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {item.kind === 'fold' &&
              foldAction({
                title: intl.formatMessage(
                  {
                    id: 'features.configurationChangelog.detail.diff.showMoreLines',
                  },
                  { count: Math.min(EXPAND_STEP, item.hiddenCount) }
                ),
                onClick: () => revealMore(item.foldStart, EXPAND_STEP),
                icon: (
                  <SvgIcon fontSize="small">
                    <IconPlus />
                  </SvgIcon>
                ),
              })}
            {item.kind === 'fold' &&
              item.revealedCount > 0 &&
              foldAction({
                title: intl.formatMessage(
                  {
                    id: 'features.configurationChangelog.detail.diff.hideMoreLines',
                  },
                  { count: Math.min(EXPAND_STEP, item.revealedCount) }
                ),
                onClick: () => revealLess(item.foldStart, EXPAND_STEP),
                icon: (
                  <SvgIcon fontSize="small">
                    <IconMinus />
                  </SvgIcon>
                ),
              })}
            {item.kind === 'fold' &&
              foldAction({
                title: intl.formatMessage(
                  {
                    id: 'features.configurationChangelog.detail.diff.showAllLines',
                  },
                  { count: item.hiddenCount }
                ),
                onClick: () => revealMore(item.foldStart, item.hiddenCount),
                icon: (
                  <SvgIcon fontSize="small">
                    <IconArrowsMoveVertical />
                  </SvgIcon>
                ),
              })}
            {item.kind === 'collapse' &&
              foldAction({
                title: intl.formatMessage({
                  id: 'features.configurationChangelog.detail.diff.hideLines',
                }),
                onClick: () => collapseFold(item.foldStart),
                icon: (
                  <SvgIcon fontSize="small">
                    <IconFold />
                  </SvgIcon>
                ),
              })}
            <Typography variant="caption" color="text.secondary">
              {item.kind === 'fold'
                ? intl.formatMessage(
                    {
                      id: 'features.configurationChangelog.detail.diff.hiddenLines',
                    },
                    { count: item.hiddenCount }
                  )
                : intl.formatMessage({
                    id: 'features.configurationChangelog.detail.diff.hideLines',
                  })}
            </Typography>
          </Stack>
        )
      )}
    </ViewerFrame>
  );
};
